import { ref, computed } from 'vue';
import { MpBattlePack, PtgBattlePack, toBundle, type BattlePack } from '@scrolldex/shared';
import { useLibrary, deriveEntryName } from '../state/library.js';
import { isGated } from '../env.js';
import { pickFileToOpen, rememberHandle } from '../utils/fileAccess.js';

// Shared bundle create/open actions. The state is module-level (a singleton) so
// any surface — the top bar switcher or the no-bundle gate — can trigger the
// "New bundle" modal or the open-from-file picker and drive the same flow. The
// New-bundle modal is rendered once by StudioBar; other callers just flip the
// shared `showNew` flag via openNew().

// Selectable battlepacks: matched play + Path to Glory enums, plus a "Custom…"
// sentinel that reveals a free-text field for a homebrew battlepack identifier.
export const mpPacks = Object.values(MpBattlePack).filter((bp) => bp !== MpBattlePack.Unknown);
export const ptgPacks = Object.values(PtgBattlePack).filter((bp) => bp !== PtgBattlePack.Unknown);
export const CUSTOM_PACK = '__custom__';

function slug(pack: string): string {
  return pack
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ─── New-bundle modal state ─────────────────────────────────────────────────
const showNew = ref(false);
// Holds an enum battlepack value, the CUSTOM_PACK sentinel, or '' (unset).
const newPackType = ref<string>('');
const newPackName = ref('');
const customPack = ref('');

// The resolved battlepack identifier (the custom string when "Custom…" is picked).
const effectivePack = computed(() =>
  newPackType.value === CUSTOM_PACK ? customPack.value.trim() : newPackType.value,
);
const newPackPlaceholder = computed(() =>
  effectivePack.value ? slug(effectivePack.value) : 'my-battlepack',
);
const canCreate = computed(() => effectivePack.value.length > 0);

export function useBundleActions() {
  const lib = useLibrary();

  function openNew() {
    // In the gated (production) config the battlepack is locked to Core.
    newPackType.value = isGated ? MpBattlePack.Core : '';
    newPackName.value = '';
    customPack.value = '';
    showNew.value = true;
  }

  function onNew() {
    const pack = effectivePack.value;
    if (!pack) return;
    const name = newPackName.value.trim() || slug(pack);
    lib.createPack(pack as BattlePack, name);
    showNew.value = false;
  }

  async function onLoad() {
    const picked = await pickFileToOpen('studio-open');
    if (!picked) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(picked.text);
    } catch {
      window.alert('That file is not valid JSON.');
      return;
    }
    const bundle = toBundle(parsed);
    if (!bundle) {
      window.alert('Unrecognized file — expected a bundle, maps file, or authored document.');
      return;
    }
    const name = deriveEntryName(picked.name);
    if (
      lib.findByName(name) &&
      !window.confirm(`A bundle named "${name}" already exists. Overwrite it?`)
    ) {
      return;
    }
    lib.applyBundleFile(bundle, name);
    // Seed this bundle's remembered export location from where it was opened, so the
    // first export starts in the same folder instead of the repo root.
    const loaded = lib.activeEntry.value;
    if (picked.handle && loaded) await rememberHandle(loaded.id, picked.handle);
  }

  return {
    // modal state (bound by StudioBar's New-bundle modal)
    showNew,
    newPackType,
    newPackName,
    customPack,
    effectivePack,
    newPackPlaceholder,
    canCreate,
    CUSTOM_PACK,
    mpPacks,
    ptgPacks,
    // actions
    openNew,
    onNew,
    onLoad,
  };
}
